<script setup lang="ts">
    import { reactive, computed, onMounted, watch } from 'vue';
    import List from '@shared/components/List/List.vue';
    import Loading from '@shared/components/Loading.vue';
    import MultiSelect from '@shared/components/MultiSelect/MultiSelect.vue';
    import BsAlert from '@shared/components/BsAlert.vue';
    import FormRadioGroup from '@shared/components/Form/FormRadioGroup.vue';
    import FormCheckable from '@shared/components/Form/FormCheckable.vue';
    import { formatDateTime } from '@cwd/Shared/utils/formatDateTime';
    import { bsAlert, AlertTypes } from '@shared/components/bsAlerts';
    import {
        getTemplateCopyAccounts,
        getIssueTemplates,
        getSurveyTemplates,
        getSmsTemplates,
        copyTemplates,
        UNGAPPED_SYSTEM_USER_ID,
        type TemplateCopyAccountItem,
        type IssueTemplateItem,
        type SurveyTemplateItem,
        type SmsTemplateItem,
    } from '../TemplateCopy.access';

    const props = defineProps<{
        clientCompanyId: string;
        serviceSubscriptionId: string;
    }>();

    const TAB_KEYS = {
        Issue: 'issue',
        Sms: 'sms',
        Survey: 'survey',
    } as const;

    type TabKey = (typeof TAB_KEYS)[keyof typeof TAB_KEYS];

    type UnifiedTemplate = {
        id: string;
        name: string;
        description: string | null;
        lastModified: string;
        lastModifiedByName: string;
        previewUrl: string | null;
        templateType: TabKey;
    };

    const state = reactive<{
        accounts: TemplateCopyAccountItem[];
        sourceAccountId: string;
        issueTemplates: IssueTemplateItem[];
        surveyTemplates: SurveyTemplateItem[];
        smsTemplates: SmsTemplateItem[];
        selectedTemplateIds: string[];
        targetAccountIds: string[];
        sendConfirmationEmail: boolean;
        lastEditedByUserId: string | null;
        activeTab: TabKey;
        loadingAccounts: boolean;
        loadingTemplates: boolean;
        submitting: boolean;
        submitted: boolean;
    }>({
        accounts: [],
        sourceAccountId: '',
        issueTemplates: [],
        surveyTemplates: [],
        smsTemplates: [],
        selectedTemplateIds: [],
        targetAccountIds: [],
        sendConfirmationEmail: true,
        lastEditedByUserId: null,
        activeTab: TAB_KEYS.Issue,
        loadingAccounts: false,
        loadingTemplates: false,
        submitting: false,
        submitted: false,
    });

    onMounted(async () => {
        state.loadingAccounts = true;
        try {
            state.accounts = await getTemplateCopyAccounts(props.clientCompanyId);
        } catch (err: any) {
            bsAlert({ type: AlertTypes.Error, content: `Could not load accounts: ${err?.message ?? err}` });
        } finally {
            state.loadingAccounts = false;
        }
    });

    watch(
        () => state.sourceAccountId,
        async (accountId) => {
            if (!accountId) return;
            state.loadingTemplates = true;
            state.selectedTemplateIds = [];
            try {
                const [issues, surveys, sms] = await Promise.all([getIssueTemplates(accountId), getSurveyTemplates(accountId), getSmsTemplates(accountId)]);
                state.issueTemplates = issues;
                state.surveyTemplates = surveys;
                state.smsTemplates = sms;
                if (issues.length > 0) state.activeTab = TAB_KEYS.Issue;
                else if (sms.length > 0) state.activeTab = TAB_KEYS.Sms;
                else if (surveys.length > 0) state.activeTab = TAB_KEYS.Survey;
            } catch (err: any) {
                bsAlert({ type: AlertTypes.Error, content: `Could not load templates: ${err?.message ?? err}` });
            } finally {
                state.loadingTemplates = false;
            }
        }
    );

    const sourceAccountSelection = computed<string[]>({
        get: () => (state.sourceAccountId ? [state.sourceAccountId] : []),
        set: (newVal: string[]) => (state.sourceAccountId = newVal[0] ?? ''),
    });

    const targetAccounts = computed<TemplateCopyAccountItem[]>(() => state.accounts.filter((a) => a.AccountId !== state.sourceAccountId));

    const sourcePublicHost = computed(() => state.accounts.find((a) => a.AccountId === state.sourceAccountId)?.PublicHost ?? '');

    const hasAnyTemplates = computed(() => state.issueTemplates.length > 0 || state.smsTemplates.length > 0 || state.surveyTemplates.length > 0);

    const templateTabs = computed(() => {
        const tabs: Partial<Record<TabKey, { name: string; display: string }>> = {};
        if (state.issueTemplates.length > 0) tabs.issue = { name: TAB_KEYS.Issue, display: 'Email templates' };
        if (state.smsTemplates.length > 0) tabs.sms = { name: TAB_KEYS.Sms, display: 'SMS templates' };
        if (state.surveyTemplates.length > 0) tabs.survey = { name: TAB_KEYS.Survey, display: 'Survey templates' };
        return tabs;
    });

    const activeTemplates = computed<UnifiedTemplate[]>(() => {
        const host = sourcePublicHost.value;
        switch (state.activeTab) {
            case TAB_KEYS.Issue:
                return state.issueTemplates.map((t) => ({
                    id: t.IssueId,
                    name: t.Subject ?? '',
                    description: t.IssueName ?? null,
                    lastModified: t.LastModified,
                    lastModifiedByName: t.LastModifiedByName,
                    previewUrl: host ? `${host}/Issues/${t.IssueId}?preview=true` : null,
                    templateType: TAB_KEYS.Issue,
                }));
            case TAB_KEYS.Sms:
                return state.smsTemplates.map((t) => ({
                    id: t.SmsMessageId,
                    name: t.Subject,
                    description: null,
                    lastModified: t.Modified,
                    lastModifiedByName: t.LastModifiedByName,
                    previewUrl: null,
                    templateType: TAB_KEYS.Sms,
                }));
            case TAB_KEYS.Survey:
                return state.surveyTemplates.map((t) => ({
                    id: t.SurveyId,
                    name: t.Title,
                    description: t.SurveyName,
                    lastModified: t.LastModified,
                    lastModifiedByName: t.LastModifiedByName,
                    previewUrl: host ? `${host}/Surveys/${t.SurveyId}?preview=true` : null,
                    templateType: TAB_KEYS.Survey,
                }));
        }
    });

    const templateTypeMap = computed(() => {
        const map = new Map<string, TabKey>();
        state.issueTemplates.forEach((t) => map.set(t.IssueId, TAB_KEYS.Issue));
        state.smsTemplates.forEach((t) => map.set(t.SmsMessageId, TAB_KEYS.Sms));
        state.surveyTemplates.forEach((t) => map.set(t.SurveyId, TAB_KEYS.Survey));
        return map;
    });

    function selectedIdsByType(type: TabKey): string[] {
        return state.selectedTemplateIds.filter((id) => templateTypeMap.value.get(id) === type);
    }

    const summaryText = computed<string>(() => {
        const issueCount = selectedIdsByType(TAB_KEYS.Issue).length;
        const smsCount = selectedIdsByType(TAB_KEYS.Sms).length;
        const surveyCount = selectedIdsByType(TAB_KEYS.Survey).length;
        const parts: string[] = [];
        if (issueCount > 0) parts.push(`${issueCount} email template${issueCount > 1 ? 's' : ''}`);
        if (smsCount > 0) parts.push(`${smsCount} SMS template${smsCount > 1 ? 's' : ''}`);
        if (surveyCount > 0) parts.push(`${surveyCount} survey template${surveyCount > 1 ? 's' : ''}`);
        if (parts.length === 0 || state.targetAccountIds.length === 0) return '';
        return `${parts.join(', ')} to ${state.targetAccountIds.length} account${state.targetAccountIds.length > 1 ? 's' : ''}`;
    });

    function selectAllTargetAccounts() {
        state.targetAccountIds = targetAccounts.value.map((a) => a.AccountId);
    }

    function deselectAllTargetAccounts() {
        state.targetAccountIds = [];
    }

    function resetForm() {
        state.sourceAccountId = '';
        state.issueTemplates = [];
        state.surveyTemplates = [];
        state.smsTemplates = [];
        state.selectedTemplateIds = [];
        state.targetAccountIds = [];
        state.sendConfirmationEmail = true;
        state.lastEditedByUserId = null;
        state.activeTab = TAB_KEYS.Issue;
        state.submitted = false;
    }

    async function submitJob() {
        if (!state.sourceAccountId) {
            bsAlert({ type: AlertTypes.Danger, content: 'Please select a source account.' });
            return;
        }
        if (state.targetAccountIds.length === 0) {
            bsAlert({ type: AlertTypes.Danger, content: 'Please select at least one target account.' });
            return;
        }
        if (state.selectedTemplateIds.length === 0) {
            bsAlert({ type: AlertTypes.Danger, content: 'Please select at least one template.' });
            return;
        }

        const selectedIssueIds = selectedIdsByType(TAB_KEYS.Issue);
        const selectedSmsIds = selectedIdsByType(TAB_KEYS.Sms);
        const selectedSurveyIds = selectedIdsByType(TAB_KEYS.Survey);

        state.submitting = true;
        try {
            await copyTemplates({
                AccountId: state.sourceAccountId,
                ClientCompanyId: props.clientCompanyId,
                ServiceSubscriptionId: props.serviceSubscriptionId,
                IssueTemplateIds: selectedIssueIds,
                SurveyTemplateIds: selectedSurveyIds,
                SmsTemplateIds: selectedSmsIds,
                TargetAccountIds: state.targetAccountIds,
                SendConfirmationEmail: state.sendConfirmationEmail,
                LastEditedByUserId: state.lastEditedByUserId,
            });
            state.submitted = true;
        } catch {
            bsAlert({ type: AlertTypes.Error, content: 'Something went wrong. Please try again.' });
        } finally {
            state.submitting = false;
        }
    }
</script>

<template>
    <BsAlert
        class="m-sm-t"
        :type="AlertTypes.Warning"
        title="Important:"
        content="Email templates will be assigned the default subscription of the receiving account. Database connections in survey templates will be lost — you need to log in to the receiving account and fix this. If a survey template uses a theme that does not exist on the receiving account, it will get Ungapped's default theme instead. Make sure you have shared the theme with the receiving account BEFORE copying the template."
        :dismissible="false" />

    <!-- Success state -->
    <BsAlert
        v-if="state.submitted"
        :type="AlertTypes.Success"
        title="Job queued!"
        :content="state.sendConfirmationEmail ? 'You will receive an email when everything is done.' : 'The templates are being copied in the background.'"
        :dismissible="false"
        buttonText="Copy again"
        @buttonClick="resetForm()"></BsAlert>

    <template v-else>
        <Loading :show="state.loadingAccounts" loadingStyle="progress-bar" fixed />

        <!-- Step 1: Source account -->
        <MultiSelect
            v-if="!state.loadingAccounts"
            class="form-group max-400"
            label="Select source account"
            :items="state.accounts"
            v-model:selectedItems="sourceAccountSelection"
            selectProp="AccountId"
            displayProp="Name"
            selectOnly="AccountId"
            :maxSelectable="1"
            placeholder="Search account..." />

        <template v-if="state.sourceAccountId">
            <Loading :show="state.loadingTemplates" loadingStyle="progress-bar" fixed />

            <BsAlert
                v-if="!state.loadingTemplates && !hasAnyTemplates"
                :type="AlertTypes.Info"
                content="This account has no email, SMS or survey templates to copy."
                :dismissible="false" />

            <template v-else-if="!state.loadingTemplates">
                <!-- Step 2: Templates -->
                <List
                    itemId="id"
                    displayAs="table"
                    :items="activeTemplates"
                    :tabs="templateTabs"
                    :currentTab="state.activeTab"
                    :checkedItems="state.selectedTemplateIds"
                    :tableHeaders="['Name', 'Description', 'Last modified by', 'Last modified']"
                    :loading="state.loadingTemplates"
                    :filterKeys="['name']"
                    :hideDelete="true"
                    :showCheckAll="true"
                    @update:tab="(tab: string) => (state.activeTab = tab as TabKey)"
                    @update:checkedItems="(ids: (string | number)[]) => (state.selectedTemplateIds = ids as string[])"
                    @clearSelected="state.selectedTemplateIds = []">
                    <template #item="{ item }">
                        <td>
                            <a v-if="item.previewUrl" :href="item.previewUrl" target="_blank" rel="noopener noreferrer" title="Preview">
                                {{ item.name || '(no name)' }}
                                <span class="glyphicon glyphicon-new-window c-light m-xs-l" aria-hidden="true"></span>
                            </a>
                            <template v-else>
                                {{ item.name || '(no name)' }}
                            </template>
                        </td>
                        <td>{{ item.description }}</td>
                        <td>{{ item.lastModifiedByName }}</td>
                        <td>{{ formatDateTime(item.lastModified) }}</td>
                    </template>
                </List>

                <!-- Step 3: Target accounts -->
                <div class="form-group d-flex gap-sm align-items-end flex-wrap max-700">
                    <div class="flex-1" style="min-width: 300px">
                        <MultiSelect
                            label="Select target account(s)"
                            :items="targetAccounts"
                            v-model:selectedItems="state.targetAccountIds"
                            selectProp="AccountId"
                            displayProp="Name"
                            selectOnly="AccountId"
                            placeholder="Search account..." />
                    </div>
                    <button class="btn btn-default" @click="selectAllTargetAccounts">Select all</button>
                    <button v-show="state.targetAccountIds.length > 0" class="btn btn-default" @click="deselectAllTargetAccounts">Deselect all</button>
                </div>

                <!-- Step 4: Ändrad av -->
                <FormRadioGroup
                    class="form-group max-400"
                    legend="Copies should be marked as last edited by"
                    :modelValue="state.lastEditedByUserId ?? ''"
                    :options="[
                        { label: `The template's user`, value: '' },
                        { label: 'Ungapped System account (it@ungapped.com)', value: UNGAPPED_SYSTEM_USER_ID },
                    ]"
                    @update:modelValue="(newVal: string) => (state.lastEditedByUserId = newVal ? newVal : null)" />

                <!-- Step 5: Confirmation email + submit -->
                <div class="form-group">
                    <FormCheckable
                        label="Send confirmation email to me when done"
                        :checked="state.sendConfirmationEmail"
                        @update:modelValue="(newVal: boolean) => (state.sendConfirmationEmail = newVal)" />
                </div>

                <p class="m-lg-b" v-show="summaryText">
                    <strong>Summary:</strong>
                    Copying {{ summaryText }}
                </p>

                <div class="form-group">
                    <button class="btn btn-primary btn-wide" :disabled="state.submitting" :class="{ 'is-busy': state.submitting }" @click="submitJob()">
                        Queue job
                    </button>
                </div>
            </template>
        </template>
    </template>
</template>
